import React, {type FC} from 'react';
import {SPECS} from '../../../../specs';
import {IImageDimension} from '../../../../types/productDef';
import {ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {IMediaItem} from '../../../../types/app-types';
import {ImageLoadingBehaviorOptions, ImageResizeOptions, WowImage} from 'wix-ui-tpa';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Maximize} from '@wix/wix-ui-icons-common/on-stage';
import {IconButton, IconButtonThemes} from 'wix-ui-tpa/cssVars';
import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import style from './ProductImage.scss';

export interface ProductImageProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  mediaItem: IMediaItem;
  imageMode: ImageMode;
  productName: string;
  dimensions?: IImageDimension;
  targetHeight?: number;
  targetWidth?: number;
  imageLoaded?(): any;
  aspectRatio?: number;
  fluid?: boolean;
  stretchImage?: boolean;
  isSelected?: boolean;
  isZoomModalEnabled?: boolean;
  onExpandIconEnterKeyPress?: () => void;
}

export enum ProductImageDataHook {
  Container = 'ProductImageDataHook.Container',
  ProductImage = 'ProductImageDataHook.ProductImage',
  ExpandIcon = 'ProductImageDataHook.ExpandIcon',
}

const ProductImageComponent: FC<ProductImageProps> = ({
  imageMode,
  mediaItem,
  targetHeight,
  targetWidth,
  imageLoaded,
  productName,
  fluid,
  stretchImage,
  isSelected,
  isZoomModalEnabled,
  onExpandIconEnterKeyPress,
  globals: {isSEO},
  t,
}) => {
  const {experiments} = useExperiments();
  const showExpandIcon = experiments.enabled(SPECS.ProductPageOOIExpandIcon) && isZoomModalEnabled;

  return (
    <div className={style.wrapper} data-hook={ProductImageDataHook.Container}>
      <WowImage
        data-hook={ProductImageDataHook.ProductImage}
        src={mediaItem.url}
        onLoad={imageLoaded}
        stretchImage={stretchImage}
        isSEOBot={isSEO}
        alt={mediaItem.altText || productName}
        sourceWidth={mediaItem.width}
        sourceHeight={mediaItem.height}
        width={targetWidth}
        height={targetHeight}
        loadingBehavior={ImageLoadingBehaviorOptions.none}
        resize={imageMode === 1 ? ImageResizeOptions.cover : ImageResizeOptions.contain}
        shouldUseLQIP={true}
        fluid={fluid}
        encoding="AVIF"
      />
      {showExpandIcon && (
        <IconButton
          as="div"
          role="button"
          icon={<Maximize />}
          data-hook={ProductImageDataHook.ExpandIcon}
          className={style.expandIcon}
          tabIndex={isSelected ? 0 : -1}
          aria-label={t('productPage.zoomModal.enlarge.ariaLabel')}
          aria-haspopup="dialog"
          theme={IconButtonThemes.Box}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onExpandIconEnterKeyPress();
            }
          }}
        />
      )}
    </div>
  );
};

export const ProductImage = withGlobalProps(withTranslations('globals.texts')(ProductImageComponent));
